import { useQuery } from '@tanstack/react-query';
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from 'recharts';
import { adminApi, mockData } from '../api/client';

const COLORS = ['#60a5fa', '#a78bfa', '#34d399', '#fbbf24', '#f87171'];

export default function Analytics() {
  const { data: analyticsData, isLoading } = useQuery({
    queryKey: ['analytics'],
    queryFn: () => adminApi.getAnalytics(30),
    placeholderData: {
      modelUsage: mockData.modelUsage(),
      creditsOverTime: mockData.creditsOverTime(),
    },
  });

  const modelUsage = analyticsData?.modelUsage || [];
  const creditsOverTime = analyticsData?.creditsOverTime || [];
  const modeUsage = mockData.modeUsage();
  const dapScores = mockData.dapScores();

  const totalCredits = modelUsage.reduce((sum: number, m: { credits: number }) => sum + m.credits, 0);
  const totalRequests = modelUsage.reduce((sum: number, m: { requests: number }) => sum + m.requests, 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-pulse text-slate-400">Loading analytics...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-white">Analytics</h1>
        <p className="text-slate-400 mt-1">Usage analytics and insights</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="card">
          <p className="text-sm font-medium text-slate-400">Total Requests</p>
          <p className="text-3xl font-bold text-white mt-2">{totalRequests.toLocaleString()}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-slate-400">Total Credits Used</p>
          <p className="text-3xl font-bold text-white mt-2">{totalCredits.toLocaleString()}</p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-slate-400">Avg Credits/Request</p>
          <p className="text-3xl font-bold text-white mt-2">
            {(totalCredits / totalRequests).toFixed(2)}
          </p>
        </div>
        <div className="card">
          <p className="text-sm font-medium text-slate-400">Avg DAP Score</p>
          <p className="text-3xl font-bold text-white mt-2">{dapScores.average.toFixed(1)}</p>
        </div>
      </div>

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Model Usage Distribution */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Model Usage Distribution</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={modelUsage}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={2}
                  dataKey="percentage"
                  nameKey="model"
                  label={({ model, percentage }: { model: string; percentage: number }) => `${model.split(' ').pop()}: ${percentage}%`}
                  labelLine={false}
                >
                  {modelUsage.map((_: unknown, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number, _name: string, props) => {
                    const payload = props?.payload as { model: string; requests: number } | undefined;
                    return payload
                      ? [`${value}% (${payload.requests.toLocaleString()} requests)`, payload.model]
                      : [String(value), ''];
                  }}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #334155', backgroundColor: '#1e293b', color: '#f1f5f9' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-2">
            {modelUsage.map((model: { model: string }, index: number) => (
              <div key={model.model} className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span className="text-sm text-slate-400">{model.model}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Credits Over Time */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Credits Over Time</h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={creditsOverTime}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  stroke="#64748b"
                  fontSize={12}
                />
                <YAxis
                  tickFormatter={(value) => `${(value / 1000).toFixed(0)}k`}
                  stroke="#64748b"
                  fontSize={12}
                />
                <Tooltip
                  formatter={(value: number) => [value.toLocaleString(), 'Credits']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #334155', backgroundColor: '#1e293b', color: '#f1f5f9' }}
                />
                <Line
                  type="monotone"
                  dataKey="credits"
                  stroke="#a78bfa"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 6, fill: '#a78bfa' }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Mode Usage */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">Mode Usage</h2>
          <div className="space-y-6">
            {modeUsage.map((mode) => (
              <div key={mode.mode} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-white capitalize">{mode.mode}</span>
                  <span className="text-sm text-slate-400">{mode.sessions.toLocaleString()} sessions</span>
                </div>
                <div className="w-full bg-slate-700 rounded-full h-4">
                  <div
                    className={`h-4 rounded-full ${mode.mode === 'collaborative' ? 'bg-primary-500' : 'bg-red-500'}`}
                    style={{ width: `${(mode.sessions / ((modeUsage[0]?.sessions ?? 0) + (modeUsage[1]?.sessions ?? 0))) * 100}%` }}
                  />
                </div>
                <p className="text-sm text-slate-400">Avg duration: {mode.avgDuration} minutes</p>
              </div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-slate-700">
            <h3 className="text-sm font-semibold text-white mb-3">Mode Comparison</h3>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-primary-500/10 rounded-lg border border-primary-500/20">
                <p className="text-sm text-primary-400">Collaborative Sessions</p>
                <p className="text-2xl font-bold text-primary-300">{(modeUsage[0]?.sessions ?? 0).toLocaleString()}</p>
                <p className="text-sm text-primary-400">
                  {((modeUsage[0]?.sessions ?? 0) / ((modeUsage[0]?.sessions ?? 0) + (modeUsage[1]?.sessions ?? 0)) * 100).toFixed(1)}% of total
                </p>
              </div>
              <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <p className="text-sm text-red-400">Adversarial Sessions</p>
                <p className="text-2xl font-bold text-red-300">{(modeUsage[1]?.sessions ?? 0).toLocaleString()}</p>
                <p className="text-sm text-red-400">
                  {((modeUsage[1]?.sessions ?? 0) / ((modeUsage[0]?.sessions ?? 0) + (modeUsage[1]?.sessions ?? 0)) * 100).toFixed(1)}% of total
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* DAP Score Distribution */}
        <div className="card">
          <h2 className="text-lg font-semibold text-white mb-4">DAP Score Distribution</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dapScores.distribution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="score" stroke="#64748b" fontSize={12} />
                <YAxis stroke="#64748b" fontSize={12} />
                <Tooltip
                  formatter={(value: number) => [value.toLocaleString(), 'Users']}
                  contentStyle={{ borderRadius: '8px', border: '1px solid #334155', backgroundColor: '#1e293b', color: '#f1f5f9' }}
                />
                <Bar dataKey="count" fill="#34d399" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-6 pt-6 border-t border-slate-700">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-sm text-slate-400">Low (1-4)</p>
                <p className="text-xl font-bold text-red-400">
                  {((dapScores.distribution[0]?.count ?? 0) + (dapScores.distribution[1]?.count ?? 0)).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-400">Medium (5-6)</p>
                <p className="text-xl font-bold text-yellow-400">
                  {(dapScores.distribution[2]?.count ?? 0).toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-400">High (7-10)</p>
                <p className="text-xl font-bold text-green-400">
                  {((dapScores.distribution[3]?.count ?? 0) + (dapScores.distribution[4]?.count ?? 0)).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Model Performance Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-white mb-4">Model Performance Details</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-700/50 border-b border-slate-700">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Model</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Requests</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Credits</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Avg Credits/Req</th>
                <th className="px-6 py-4 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider">Usage %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {modelUsage.map((model: { model: string; requests: number; credits: number; percentage: number }, index: number) => (
                <tr key={model.model} className="hover:bg-slate-700/50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                      />
                      <span className="font-medium text-white">{model.model}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-slate-300">
                    {model.requests.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-slate-300">
                    {model.credits.toLocaleString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-slate-300">
                    {(model.credits / model.requests).toFixed(2)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-slate-700 rounded-full h-2">
                        <div
                          className="h-2 rounded-full"
                          style={{
                            width: `${model.percentage}%`,
                            backgroundColor: COLORS[index % COLORS.length],
                          }}
                        />
                      </div>
                      <span className="text-slate-300">{model.percentage}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
